import FormSection from "../../../common/components/functional/formSection/FormSection";
import { useContext, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Backdrop,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import LoadingSpinner from "../../../common/components/functional/loadingSpinner/LoadingSpinner";
import Checkbox from "@mui/material/Checkbox";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  selectTalentById,
  updateTalentApplicationLanguage,
  updateTalentGdprProperties,
} from "../../talents/talentsSlice";
import { Talent, talentIsDeactivated } from "../../talents/talentAPI";
import isEmpty from "../../../common/helpers/isEmpty";
import {
  UpdateTalentApplicationLanguage,
  UpdateTalentGdprProperties,
} from "../../talents/types/commands";
import { isFulfilled } from "@reduxjs/toolkit";
import BulletDescription from "./BulletDescription";
import { AccessRightContext } from "../../user/AccessRightProvider";
import { UnsavedChangesProvider } from "../../../contexts/UnsavedChangesContext";

type FormValues = {
  username: string;
  applicationLanguage: string;
  disableAutomatching: boolean;
  forbidPersonalInfoUpdates: boolean;
};

const TalentUserAccount = () => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const { canEditTalentProfile } = useContext(AccessRightContext);
  const { talentId } = useParams();

  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );

  const dispatch = useAppDispatch();

  const availableApplicationLanguages = ["fi", "en"];

  const talentApplicationLanguage = talent.applicationLanguage;
  const talentGdprProperties = talent.gdprProperties;

  const defaultValues = useMemo(
    () => ({
      username: talent.account.emailAddress,
      applicationLanguage: talentApplicationLanguage?.value || "",
      disableAutomatching: talentGdprProperties?.disableAutomatching,
      forbidPersonalInfoUpdates:
        talentGdprProperties?.forbidPersonalInfoUpdates,
    }),
    [
      talent.account.emailAddress,
      talentApplicationLanguage,
      talentGdprProperties,
    ],
  );

  const methods = useForm<FormValues>({
    defaultValues: defaultValues,
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { errors, isDirty, dirtyFields },
  } = methods;

  const onSubmit = (data: FormValues) => {
    setIsLoading(true);

    if (talentId) {
      const gdprPropertiesPayload: UpdateTalentGdprProperties = {
        talentId: talentId,
        version: talentGdprProperties.version,
        disableAutomatching: data.disableAutomatching,
        forbidPersonalInfoUpdates: data.forbidPersonalInfoUpdates,
      };

      const applicationLanguagePayload: UpdateTalentApplicationLanguage = {
        talentId: talentId,
        version: talentApplicationLanguage?.version || 0,
        language: data.applicationLanguage,
      };

      const shouldUpdateApplicationLanguage = dirtyFields.applicationLanguage;
      const shouldUpdateGdprProperties =
        dirtyFields.disableAutomatching ||
        dirtyFields.forbidPersonalInfoUpdates;

      const dispatchApplicationLanguageUpdate = () =>
        shouldUpdateApplicationLanguage
          ? dispatch(
              updateTalentApplicationLanguage(applicationLanguagePayload),
            ).then((action) => isFulfilled(action))
          : Promise.resolve(true);

      const dispatchGdprPropertiesUpdate = () =>
        shouldUpdateGdprProperties
          ? dispatch(updateTalentGdprProperties(gdprPropertiesPayload)).then(
              (action) => isFulfilled(action),
            )
          : Promise.resolve(true);

      dispatchApplicationLanguageUpdate()
        .finally(() => dispatchGdprPropertiesUpdate())
        .finally(() => {
          reset(getValues());
          setIsLoading(false);
          setEditing(false);
        });
    }
  };

  const onSave = () => {
    editing ? handleSubmit(onSubmit)() : setEditing(true);
  };
  const onCancel = () => {
    setEditing(false);
    reset(defaultValues);
  };

  const selectionFor = (languageCode: string) => (
    <MenuItem key={languageCode} value={languageCode}>
      {t("common:" + languageCode)}
    </MenuItem>
  );

  const saveDisabled = useMemo(
    () => !isEmpty(errors) || !isDirty || isLoading,
    [errors, isDirty, isLoading],
  );

  const title = t("accountSettings:userAccount");
  const username = t("accountSettings:username");
  const applicationLanguage = t("accountSettings:applicationLanguage:title");

  return (
    <FormSection
      title={title}
      margin
      noBorder
      edit={editing}
      onEdit={onSave}
      onCancel={onCancel}
      saveDisabled={saveDisabled}
      readOnly={!canEditTalentProfile || talentIsDeactivated(talent)}
    >
      <Backdrop
        open={isLoading}
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.4)",
          zIndex: 1000,
        }}
      >
        <LoadingSpinner />
      </Backdrop>
      <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
        <Grid
          item
          container
          xs={3}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Typography textAlign="end" variant="secondary">
            {username}
          </Typography>
        </Grid>
        <Grid item xs={9}>
          <Controller
            name="username"
            control={control}
            render={({ field: { value } }) => <Typography>{value}</Typography>}
          />
        </Grid>
        <Grid
          item
          container
          xs={3}
          justifyContent="flex-end"
          alignItems="flex-start"
        >
          <Typography
            textAlign="end"
            variant="secondary"
            sx={{ paddingTop: editing ? "7px" : 0 }}
          >
            {applicationLanguage}
          </Typography>
        </Grid>
        <Grid item container xs={9} alignItems="flex-start">
          <Controller
            name="applicationLanguage"
            control={control}
            render={({ field: { value, onChange } }) =>
              editing ? (
                <>
                  <Grid item container xs={7}>
                    <FormControl fullWidth size="small">
                      <Select value={value} onChange={onChange}>
                        {availableApplicationLanguages.map((languageCode) =>
                          selectionFor(languageCode),
                        )}
                      </Select>
                    </FormControl>
                    <FormHelperText
                      sx={{ fontSize: "12px", paddingLeft: "15px" }}
                    >
                      {t("accountSettings:applicationLanguage:changeNote")}
                    </FormHelperText>
                  </Grid>
                </>
              ) : (
                <Typography>{value ? t("common:" + value) : ""}</Typography>
              )
            }
          />
        </Grid>
        <Grid item xs={3} />
        <Grid item container xs={9}>
          <Grid item container>
            <Controller
              control={control}
              name="disableAutomatching"
              render={({ field: { onChange, value } }) => (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={!editing}
                        sx={{ padding: "2px 8px" }}
                        checked={value}
                        onChange={onChange}
                      />
                    }
                    label={
                      <Typography>
                        {t("accountSettings:gdpr:talentInfo:title")}
                      </Typography>
                    }
                  />
                </>
              )}
            />
          </Grid>
          <Typography sx={{ marginLeft: "30px" }} variant="secondary">
            {t("accountSettings:gdpr:talentInfo:description")}
          </Typography>
        </Grid>
        <Grid item xs={3} />
        <Grid
          item
          container
          xs={9}
          justifyContent="flex-start"
          alignItems="center"
        >
          <Grid item container alignItems="center">
            <Controller
              control={control}
              name="forbidPersonalInfoUpdates"
              render={({ field: { onChange, value } }) => (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={!editing}
                        sx={{ padding: "2px 8px" }}
                        checked={value}
                        onChange={onChange}
                      />
                    }
                    label={
                      <Typography>
                        {t("accountSettings:gdpr:personalInfo:title")}
                      </Typography>
                    }
                  />
                </>
              )}
            />
          </Grid>
          <BulletDescription />
        </Grid>
      </UnsavedChangesProvider>
    </FormSection>
  );
};

export default TalentUserAccount;
